import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Download, CheckCircle, Clock, XCircle, DollarSign, Eye, Printer,
  Mail, FileText, Send, Check, CreditCard, Banknote, Building2, Plus, Trash2,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import api from '../utils/api';
import { formatCurrency, formatDate, formatDateTime, getStatusLabel, getStatusColor } from '../utils/helpers';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';

export const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [workOrders, setWorkOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Overrides state
  const [overrides, setOverrides] = useState({});
  const [editingField, setEditingField] = useState(null); // { woId, field }
  const [editValue, setEditValue] = useState('');

  // Note line editing
  const [editingNoteLine, setEditingNoteLine] = useState(null);
  const [noteLineValue, setNoteLineValue] = useState('');
  // Item line notes editing
  const [editingItemNote, setEditingItemNote] = useState(null);
  const [itemNoteValue, setItemNoteValue] = useState('');

  // Send dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingMail, setMarkingMail] = useState(false);

  // Payment dialog
  const [payments, setPayments] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({ payment_date: new Date().toISOString().split('T')[0], amount_chf: 0, method: 'BANK_TRANSFER', notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);

  const isEditable = invoice?.status === 'DRAFT';

  useEffect(() => { loadInvoice(); }, [id]); // eslint-disable-line

  const loadInvoice = async () => {
    try {
      const response = await api.getInvoice(id);
      setInvoice(response.data);
      setOverrides(response.data.work_order_overrides || {});

      const customerResponse = await api.getCustomers();
      const cust = customerResponse.data.find(c => c.id === response.data.customer_id);
      setCustomer(cust);

      try { const pr = await api.getInvoicePayments(id); setPayments(pr.data || []); } catch(e) { setPayments([]); }

      if (response.data.work_order_ids?.length > 0) {
        const woMap = {};
        for (const woId of response.data.work_order_ids) {
          try {
            const woResponse = await api.getWorkOrder(woId);
            woMap[woId] = {
              number: woResponse.data.number,
              site_name: woResponse.data.site_name || null,
              site_reference: woResponse.data.site_reference || null,
              notes: woResponse.data.notes || null
            };
          } catch(e) { woMap[woId] = { number: woId }; }
        }
        setWorkOrders(woMap);
      }
    } catch (error) {
      toast.error('Errore nel caricamento fattura');
      navigate('/app/invoices');
    } finally {
      setLoading(false);
    }
  };

  // === Override editing ===
  const startEditing = (woId, field, currentValue) => { setEditingField({ woId, field }); setEditValue(currentValue || ''); };
  const saveEdit = async () => {
    if (!editingField) return;
    const { woId, field } = editingField;
    try {
      const newOverrides = { ...overrides, [woId]: { ...(overrides[woId] || {}), [field]: editValue.trim() } };
      await api.updateInvoiceOverrides(id, newOverrides);
      setOverrides(newOverrides);
      toast.success('Salvato');
    } catch (error) { toast.error('Errore nel salvataggio'); }
    finally { setEditingField(null); setEditValue(''); }
  };
  const cancelEdit = () => { setEditingField(null); setEditValue(''); };
  const handleEditKeyDown = (e) => { if (e.key === 'Enter') saveEdit(); else if (e.key === 'Escape') cancelEdit(); };
  const getDisplayValue = (woId, field, originalValue) => {
    const wo = overrides[woId];
    if (wo && field in wo) return wo[field];
    return originalValue || '';
  };

  // === Line reordering ===
  const moveLine = async (lineId, direction) => {
    const lines = [...invoice.lines];
    const index = lines.findIndex(l => l.id === lineId);
    if (index === -1) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= lines.length) return;
    [lines[index], lines[newIndex]] = [lines[newIndex], lines[index]];
    const lineOrders = lines.map((l, i) => ({ id: l.id, sort_order: i }));
    try { await api.reorderInvoiceLines(id, lineOrders); loadInvoice(); } catch (error) { toast.error('Errore nel riordino'); }
  };

  // === NOTE lines ===
  const addNoteLine = async (afterLineId = null) => {
    try {
      let sortOrder = invoice.lines.length;
      if (afterLineId) {
        const afterIndex = invoice.lines.findIndex(l => l.id === afterLineId);
        if (afterIndex !== -1) {
          sortOrder = afterIndex + 1;
          const lineOrders = invoice.lines.map((l, i) => ({ id: l.id, sort_order: i >= sortOrder ? i + 1 : i }));
          await api.reorderInvoiceLines(id, lineOrders);
        }
      }
      await api.addInvoiceLine(id, { description: '', sort_order: sortOrder });
      loadInvoice();
      toast.success('Riga nota aggiunta');
    } catch (error) { toast.error("Errore nell'aggiunta"); }
  };

  const deleteNoteLine = async (lineId) => {
    const ok = await confirm({
      title: 'Elimina riga nota',
      description: 'La riga nota sarà rimossa dalla fattura.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteInvoiceLine(id, lineId); loadInvoice(); toast.success('Riga eliminata'); }
    catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  const saveNoteLine = async () => {
    if (!editingNoteLine) return;
    try { await api.updateInvoiceLine(id, editingNoteLine, { description: noteLineValue }); setEditingNoteLine(null); setNoteLineValue(''); loadInvoice(); }
    catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const saveItemNote = async () => {
    if (!editingItemNote) return;
    try { await api.updateInvoiceLine(id, editingItemNote, { line_notes: itemNoteValue }); setEditingItemNote(null); setItemNoteValue(''); loadInvoice(); }
    catch (error) { toast.error('Errore nel salvataggio'); }
  };

  // Persist a single field (unit_price_chf or mwst_rate_snapshot) on an invoice
  // line — used to complete free articles before the invoice can be issued.
  const saveLineField = async (lineId, field, value) => {
    try {
      await api.updateInvoiceLine(id, lineId, { [field]: value });
      loadInvoice();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // === WO Info Layout (common vs specific) ===
  const calculateWoInfoLayout = () => {
    const woIds = invoice?.work_order_ids || [];
    if (woIds.length === 0) return { commonInfo: null, specificInfo: {} };

    const allSites = woIds.map(woId => getDisplayValue(woId, 'site_display', (workOrders[woId] || {}).site_name));
    const allRefs = woIds.map(woId => getDisplayValue(woId, 'reference_display', (workOrders[woId] || {}).site_reference));
    const allNotes = woIds.map(woId => getDisplayValue(woId, 'notes_display', (workOrders[woId] || {}).notes));

    if (woIds.length === 1) {
      const parts = [];
      if (allSites[0]) parts.push({ label: 'Progetto', value: allSites[0] });
      if (allRefs[0]) parts.push({ label: 'Riferimento', value: allRefs[0] });
      if (allNotes[0]) parts.push({ label: 'Note', value: allNotes[0] });
      return { commonInfo: parts.length > 0 ? parts : null, specificInfo: {} };
    }

    const uniqueSites = [...new Set(allSites)];
    const uniqueRefs = [...new Set(allRefs)];
    const uniqueNotes = [...new Set(allNotes)];
    const siteCommon = uniqueSites.length === 1 && uniqueSites[0];
    const refCommon = uniqueRefs.length === 1 && uniqueRefs[0];
    const notesCommon = uniqueNotes.length === 1 && uniqueNotes[0];

    const commonParts = [];
    if (siteCommon) commonParts.push({ label: 'Progetto', value: uniqueSites[0] });
    if (refCommon) commonParts.push({ label: 'Riferimento', value: uniqueRefs[0] });
    if (notesCommon) commonParts.push({ label: 'Note', value: uniqueNotes[0] });

    const specificInfo = {};
    woIds.forEach((woId, idx) => {
      const parts = [];
      if (!siteCommon && allSites[idx]) parts.push({ label: 'Progetto', value: allSites[idx] });
      if (!refCommon && allRefs[idx]) parts.push({ label: 'Riferimento', value: allRefs[idx] });
      if (!notesCommon && allNotes[idx]) parts.push({ label: 'Note', value: allNotes[idx] });
      if (parts.length > 0) specificInfo[woId] = parts;
    });

    return { commonInfo: commonParts.length > 0 ? commonParts : null, specificInfo };
  };

  // === PDF / Actions ===
  const handleDownloadPDF = async () => {
    try {
      const response = await api.downloadInvoicePDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = `${invoice.number}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (error) { toast.error('Errore nel download PDF'); }
  };
  const handleShowPDF = async () => {
    try { const r = await api.downloadInvoicePDF(id); window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank'); }
    catch (error) { toast.error("Errore nell'apertura PDF"); }
  };
  const handlePrint = async () => {
    try { const r = await api.downloadInvoicePDF(id); const w = window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank'); if(w) w.onload = () => w.print(); }
    catch (error) { toast.error('Errore nella stampa'); }
  };
  const handleIssue = async () => {
    try { setActionLoading(true); await api.issueInvoice(id); toast.success('Fattura emessa'); await loadInvoice(); setShowSendDialog(true); }
    catch (error) { toast.error(error.response?.data?.detail || "Errore nell'emissione"); }
    finally { setActionLoading(false); }
  };
  const handleSendEmail = async () => {
    try { setSendingEmail(true); const r = await api.sendInvoiceEmail(id); toast.success(r.data.message || 'Email inviata'); await loadInvoice(); }
    catch (error) { toast.error(error.response?.data?.detail || "Errore nell'invio email"); }
    finally { setSendingEmail(false); }
  };
  const handleMarkMailSent = async () => {
    try { setMarkingMail(true); await api.markInvoiceMailSent(id); toast.success('Fattura segnata come inviata per posta'); await loadInvoice(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
    finally { setMarkingMail(false); }
  };
  const handleSettle = () => {
    const tp = payments.reduce((s, p) => s + p.amount_chf, 0);
    setPaymentData({ payment_date: new Date().toISOString().split('T')[0], amount_chf: Math.max(0, (invoice?.gross_total || 0) - tp), method: 'BANK_TRANSFER', notes: '' });
    setShowPaymentDialog(true);
  };
  const handleRegisterPayment = async () => {
    if (!paymentData.amount_chf || paymentData.amount_chf <= 0) { toast.error('Inserire un importo valido'); return; }
    try { setSavingPayment(true); await api.registerPayment(id, paymentData); toast.success('Pagamento registrato'); setShowPaymentDialog(false); await loadInvoice(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Errore nella registrazione'); }
    finally { setSavingPayment(false); }
  };
  const handleCancel = async () => {
    const isDraft = invoice?.status === 'DRAFT';
    const ok = await confirm({
      title: isDraft ? 'Annulla bozza di fattura?' : 'Annulla fattura?',
      description: isDraft
        ? 'La bozza sarà annullata e i bollettini collegati torneranno disponibili per generare una nuova fattura.'
        : 'La fattura sarà marcata come annullata e i bollettini collegati torneranno disponibili per generare una nuova fattura.',
      confirmText: isDraft ? 'Annulla bozza' : 'Annulla fattura',
      destructive: true,
    });
    if (!ok) return;
    try { setActionLoading(true); await api.cancelInvoice(id); toast.success(isDraft ? 'Bozza annullata' : 'Fattura annullata'); loadInvoice(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="p-8">Caricamento...</div>;
  if (!invoice) return <div className="p-8">Fattura non trovata</div>;

  const woInfoLayout = calculateWoInfoLayout();
  const totalPaid = payments.reduce((s, p) => s + p.amount_chf, 0);
  const totalInvoice = invoice.gross_total || 0;
  const paymentPercentage = totalInvoice > 0 ? Math.min(100, (totalPaid / totalInvoice) * 100) : 0;
  const remainingAmount = Math.max(0, totalInvoice - totalPaid);

  return (
    <div data-testid="invoice-detail-page">
      {/* Header */}
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate('/app/invoices')} className="mb-4" data-testid="back-to-invoices">
          <ArrowLeft size={18} className="mr-2" /> Torna alle Fatture
        </Button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{invoice.number}</h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <span className={`status-badge ${getStatusColor(invoice.status)}`}>{getStatusLabel(invoice.status)}</span>
              {invoice.issue_date && <span className="text-sm text-slate-500">Emessa il {formatDate(invoice.issue_date)}</span>}
            </div>
            {/* Sending status indicators */}
            {(invoice.status === 'ISSUED' || invoice.status === 'OVERDUE' || invoice.status === 'SETTLED') && (
              <div className="mt-2 flex items-center gap-3 flex-wrap text-xs" data-testid="send-status-indicators">
                {(customer?.invoice_delivery_method === 'EMAIL' || customer?.invoice_delivery_method === 'BOTH') && (
                  invoice.email_sent_at
                    ? <span className="flex items-center gap-1 text-green-600"><Check size={12} /> Email inviata {formatDate(invoice.email_sent_at)}</span>
                    : customer?.billing_email
                      ? <span className="flex items-center gap-1 text-amber-600"><Mail size={12} /> Email da inviare</span>
                      : <span className="flex items-center gap-1 text-red-500"><Mail size={12} /> Email mancante</span>
                )}
                {(customer?.invoice_delivery_method === 'PAPER' || customer?.invoice_delivery_method === 'BOTH') && (
                  invoice.mail_sent_at
                    ? <span className="flex items-center gap-1 text-green-600"><Check size={12} /> Posta inviata {formatDate(invoice.mail_sent_at)}</span>
                    : <span className="flex items-center gap-1 text-amber-600"><FileText size={12} /> Posta da inviare</span>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleShowPDF}><Eye size={16} className="mr-1" /> PDF</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Download size={16} className="mr-1" /> Esporta</Button></DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleDownloadPDF}><Download size={14} className="mr-2" /> Scarica PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={handlePrint}><Printer size={14} className="mr-2" /> Stampa</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {invoice.status === 'DRAFT' && (
              <>
                <Button onClick={handleIssue} disabled={actionLoading} data-testid="issue-invoice-btn"><CheckCircle size={16} className="mr-1" /> Emetti Fattura</Button>
                <Button onClick={handleCancel} variant="outline" className="text-red-600 hover:bg-red-50" disabled={actionLoading} data-testid="cancel-draft-invoice-btn"><XCircle size={16} className="mr-1" /> Annulla bozza</Button>
              </>
            )}
            {(invoice.status === 'ISSUED' || invoice.status === 'OVERDUE') && (
              <>
                {(() => {
                  const dm = customer?.invoice_delivery_method;
                  const canEmail = customer?.billing_email && (dm === 'EMAIL' || dm === 'BOTH');
                  const canMail = dm === 'PAPER' || dm === 'BOTH';
                  const emailPending = canEmail && !invoice.email_sent_at;
                  const mailPending = canMail && !invoice.mail_sent_at;
                  if (dm === 'BOTH' && (emailPending || mailPending)) {
                    return (<DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Send size={16} className="mr-1" /> Invia</Button></DropdownMenuTrigger><DropdownMenuContent>
                      {emailPending && <DropdownMenuItem onClick={handleSendEmail}><Mail size={14} className="mr-2" />{sendingEmail ? 'Invio...' : 'Invia Email'}</DropdownMenuItem>}
                      {mailPending && <DropdownMenuItem onClick={handleMarkMailSent}><FileText size={14} className="mr-2" />{markingMail ? 'Salvo...' : 'Segna Posta'}</DropdownMenuItem>}
                    </DropdownMenuContent></DropdownMenu>);
                  }
                  if (emailPending && dm === 'EMAIL') return <Button variant="outline" size="sm" onClick={handleSendEmail} disabled={sendingEmail}><Mail size={16} className="mr-1" />{sendingEmail ? 'Invio...' : 'Invia Email'}</Button>;
                  if (mailPending && dm === 'PAPER') return <Button variant="outline" size="sm" onClick={handleMarkMailSent} disabled={markingMail}><FileText size={16} className="mr-1" />{markingMail ? 'Salvo...' : 'Segna Posta'}</Button>;
                  return null;
                })()}
                <Button onClick={handleSettle} className="bg-green-600 hover:bg-green-700" disabled={actionLoading}><DollarSign size={16} className="mr-1" /> Salda</Button>
                <Button onClick={handleCancel} variant="outline" className="text-red-600 hover:bg-red-50" disabled={actionLoading}><XCircle size={16} className="mr-1" /> Annulla</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Destinatario</CardTitle></CardHeader>
        <CardContent>
          {customer ? (
            <div className="flex justify-between">
              <div>
                <p className="font-medium text-lg">{customer.name_or_company}</p>
                <div className="text-slate-600 mt-1">
                  {(customer.billing_address_street || customer.address_street) && <p>{customer.billing_address_street || customer.address_street}</p>}
                  {(customer.billing_address_zip || customer.billing_address_city || customer.address_zip || customer.address_city) && (
                    <p>{customer.billing_address_zip || customer.address_zip} {customer.billing_address_city || customer.address_city}</p>
                  )}
                  {(customer.billing_address_canton || customer.address_canton) && (
                    <p>{customer.billing_address_canton || customer.address_canton}, {customer.billing_address_country || customer.address_country || 'CH'}</p>
                  )}
                </div>
                {customer.billing_email && <p className="text-slate-500 text-sm mt-1">{customer.billing_email}</p>}
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 mb-1">Metodo invio fattura</p>
                <div className="flex items-center justify-end gap-2">
                  {customer.invoice_delivery_method === 'EMAIL' ? (<><Mail size={16} className="text-blue-500" /><span className="text-slate-700 font-medium">Email</span></>) :
                   customer.invoice_delivery_method === 'BOTH' ? (<><Mail size={16} className="text-blue-500" /><span className="text-slate-700 font-medium">Email + Posta</span></>) :
                   (<><FileText size={16} className="text-slate-500" /><span className="text-slate-700 font-medium">Cartaceo</span></>)}
                </div>
              </div>
            </div>
          ) : <p className="text-slate-500">Cliente non trovato</p>}
        </CardContent>
      </Card>

      {/* Work Orders Reference with editable overrides */}
      {invoice.work_order_ids?.length > 0 && (
        <Card className="mb-6">
          <CardHeader><CardTitle>Bollettini Inclusi</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoice.work_order_ids.map((woId) => {
                const wo = workOrders[woId] || {};
                const isEditingSite = editingField?.woId === woId && editingField?.field === 'site_display';
                const isEditingRef = editingField?.woId === woId && editingField?.field === 'reference_display';
                const isEditingNotes = editingField?.woId === woId && editingField?.field === 'notes_display';
                const siteDisplay = getDisplayValue(woId, 'site_display', wo.site_name);
                const refDisplay = getDisplayValue(woId, 'reference_display', wo.site_reference);
                const notesDisplay = getDisplayValue(woId, 'notes_display', wo.notes);

                return (
                  <div key={woId} className="flex items-start gap-3 flex-wrap py-2 border-b last:border-0">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/app/work-orders/${woId}`)} className="shrink-0">
                      {wo.number || woId}
                    </Button>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm flex-1">
                      {/* Progetto */}
                      {(siteDisplay || (isEditable && isEditingSite)) ? (
                        <span className="flex items-center gap-1">
                          <span className="text-slate-500">Progetto:</span>
                          {isEditable && isEditingSite ? (
                            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={handleEditKeyDown} autoFocus className="w-64 h-7 text-sm" />
                          ) : (
                            <span onClick={isEditable ? () => startEditing(woId, 'site_display', siteDisplay) : undefined}
                              className={`text-slate-600 px-2 py-0.5 rounded ${isEditable ? 'border border-dashed border-slate-300 cursor-pointer hover:border-slate-400 hover:bg-secondary' : ''}`}
                              title={isEditable ? "Clicca per modificare" : ""}>{siteDisplay}</span>
                          )}
                        </span>
                      ) : isEditable ? (
                        <span onClick={() => startEditing(woId, 'site_display', '')} className="text-sm text-slate-400 cursor-pointer border border-dashed border-slate-300 px-2 py-0.5 rounded hover:border-slate-400 hover:bg-secondary">+ Progetto</span>
                      ) : null}

                      {/* Riferimento */}
                      {(refDisplay || (isEditable && isEditingRef)) ? (
                        <span className="flex items-center gap-1">
                          <span className="text-slate-500">Rif:</span>
                          {isEditable && isEditingRef ? (
                            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={handleEditKeyDown} autoFocus className="w-32 h-7 text-sm" />
                          ) : (
                            <span onClick={isEditable ? () => startEditing(woId, 'reference_display', refDisplay) : undefined}
                              className={`text-slate-600 px-2 py-0.5 rounded ${isEditable ? 'border border-dashed border-slate-300 cursor-pointer hover:border-slate-400 hover:bg-secondary' : ''}`}
                              title={isEditable ? "Clicca per modificare" : ""}>{refDisplay}</span>
                          )}
                        </span>
                      ) : isEditable ? (
                        <span onClick={() => startEditing(woId, 'reference_display', '')} className="text-sm text-slate-400 cursor-pointer border border-dashed border-slate-300 px-2 py-0.5 rounded hover:border-slate-400 hover:bg-secondary">+ Riferimento</span>
                      ) : null}

                      {/* Note */}
                      {isEditable && isEditingNotes ? (
                        <span className="flex items-center gap-1"><span className="text-slate-500">Nota:</span>
                          <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={handleEditKeyDown} autoFocus className="w-64 h-7 text-sm" />
                        </span>
                      ) : notesDisplay ? (
                        <span className="flex items-center gap-1"><span className="text-slate-500">Nota:</span>
                          <span onClick={isEditable ? () => startEditing(woId, 'notes_display', notesDisplay) : undefined}
                            className={`text-slate-600 italic px-2 py-0.5 rounded ${isEditable ? 'border border-dashed border-slate-300 cursor-pointer hover:border-slate-400 hover:bg-secondary' : ''}`}
                            title={isEditable ? "Clicca per modificare" : ""}>{notesDisplay}</span>
                        </span>
                      ) : isEditable ? (
                        <span onClick={() => startEditing(woId, 'notes_display', '')} className="text-sm text-slate-400 cursor-pointer border border-dashed border-slate-300 px-2 py-0.5 rounded hover:border-slate-400 hover:bg-secondary">+ Nota</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice Lines - Full table with Data, WO, reorder, NOTE lines */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Righe Fattura</CardTitle>
            {isEditable && (
              <Button variant="outline" size="sm" onClick={() => addNoteLine()} data-testid="add-note-line-btn">
                <Plus size={16} className="mr-1" /> Aggiungi riga nota
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Common WO Info Header */}
          {woInfoLayout.commonInfo && (
            <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100 text-sm flex flex-wrap gap-4">
              {woInfoLayout.commonInfo.map((item, idx) => (
                <span key={idx}><span className="font-medium text-blue-800">{item.label}:</span>{' '}<span className="text-blue-700">{item.value}</span></span>
              ))}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b">
                <tr>
                  {isEditable && <th className="w-10 p-2"></th>}
                  <th className="text-left p-3 text-xs font-semibold text-slate-700">Data</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-700">WO</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-700">Descrizione</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-700">Note</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-700">U.M.</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-700">Quantita</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-700">Prezzo Unit.</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-700">Sconto</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-700">IVA %</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-700">Netto</th>
                  {isEditable && <th className="w-10 p-2"></th>}
                </tr>
              </thead>
              <tbody>
                {invoice.lines?.map((line, idx) => {

                  // WO_HEADER line
                  if (line.line_type === 'WO_HEADER') {
                    const woId = line.work_order_id;
                    const specificParts = woInfoLayout.specificInfo[woId];
                    if (!specificParts || specificParts.length === 0) return null;
                    const displayText = specificParts.map(p => `${p.label}: ${p.value}`).join(' | ');
                    return (
                      <tr key={line.id || idx} className="border-b bg-blue-50/50">
                        {isEditable && (
                          <td className="p-1 text-center">
                            <button onClick={() => moveLine(line.id, 'up')} disabled={idx === 0} className="p-0.5 hover:bg-blue-200 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                            <button onClick={() => moveLine(line.id, 'down')} disabled={idx === invoice.lines.length - 1} className="p-0.5 hover:bg-blue-200 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                          </td>
                        )}
                        <td className="p-2 text-xs text-slate-400">-</td>
                        <td className="p-2 text-xs font-mono">{line.work_order_number ? line.work_order_number.slice(-4) : '-'}</td>
                        <td colSpan={isEditable ? 8 : 7} className="p-2 text-xs text-blue-700 font-medium">{displayText}</td>
                        {isEditable && <td></td>}
                      </tr>
                    );
                  }

                  // NOTE line
                  if (line.line_type === 'NOTE') {
                    return (
                      <tr key={line.id || idx} className="border-b bg-amber-50/50">
                        {isEditable && (
                          <td className="p-1 text-center">
                            <button onClick={() => moveLine(line.id, 'up')} disabled={idx === 0} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                            <button onClick={() => moveLine(line.id, 'down')} disabled={idx === invoice.lines.length - 1} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                          </td>
                        )}
                        <td className="p-2 text-xs text-slate-400">-</td>
                        <td className="p-2 text-xs text-slate-400">-</td>
                        <td colSpan={isEditable ? 7 : 6} className="p-2">
                          {isEditable && editingNoteLine === line.id ? (
                            <Input value={noteLineValue} onChange={(e) => setNoteLineValue(e.target.value)}
                              onBlur={saveNoteLine} onKeyDown={(e) => { if (e.key === 'Enter') saveNoteLine(); if (e.key === 'Escape') { setEditingNoteLine(null); setNoteLineValue(''); }}}
                              autoFocus placeholder="Inserisci testo nota o lascia vuoto per spazio..." className="w-full font-semibold text-slate-800" />
                          ) : (
                            <span onClick={isEditable ? () => { setEditingNoteLine(line.id); setNoteLineValue(line.description || ''); } : undefined}
                              className={`px-2 py-1 rounded min-h-[28px] inline-block font-semibold text-slate-800 text-sm ${isEditable ? 'cursor-pointer hover:bg-amber-100' : ''}`}
                              title={isEditable ? "Clicca per modificare" : ""}>
                              {line.description || <span className="text-slate-400 font-normal">{isEditable ? 'Clicca per aggiungere testo...' : '(spazio vuoto)'}</span>}
                            </span>
                          )}
                        </td>
                        {isEditable && <td className="p-2"><button onClick={() => deleteNoteLine(line.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Elimina riga nota"><Trash2 size={14} /></button></td>}
                      </tr>
                    );
                  }

                  // ITEM line
                  const isFree = !!line.is_free_article;
                  const missingPrice = isFree && (line.unit_price_chf == null);
                  const missingIva = isFree && (line.mwst_rate_snapshot == null);
                  return (
                    <tr key={line.id || idx} className={`border-b ${isFree ? 'bg-amber-50/60' : ''}`} data-testid={isFree ? `free-invoice-line-${idx}` : undefined}>
                      {isEditable && (
                        <td className="p-1 text-center">
                          <button onClick={() => moveLine(line.id, 'up')} disabled={idx === 0} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30" title="Sposta su"><ChevronUp size={14} /></button>
                          <button onClick={() => moveLine(line.id, 'down')} disabled={idx === invoice.lines.length - 1} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30" title="Sposta giu"><ChevronDown size={14} /></button>
                        </td>
                      )}
                      <td className="p-2 text-xs text-slate-500 whitespace-nowrap">{line.work_order_date ? formatDate(line.work_order_date).slice(0, 5) : '-'}</td>
                      <td className="p-2 text-xs font-mono text-slate-500">{line.work_order_number ? line.work_order_number.slice(-4) : '-'}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <span>{line.description}</span>
                          {isFree && (
                            <span className="inline-flex items-center self-start text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                              Articolo libero
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {isEditable && editingItemNote === line.id ? (
                          <Input value={itemNoteValue} onChange={(e) => setItemNoteValue(e.target.value)}
                            onBlur={saveItemNote} onKeyDown={(e) => { if (e.key === 'Enter') saveItemNote(); if (e.key === 'Escape') { setEditingItemNote(null); setItemNoteValue(''); }}}
                            autoFocus className="w-full h-7 text-sm" />
                        ) : (
                          <span onClick={isEditable ? () => { setEditingItemNote(line.id); setItemNoteValue(line.line_notes || ''); } : undefined}
                            className={`text-sm text-slate-600 px-2 py-0.5 rounded inline-block min-w-[60px] ${isEditable ? 'cursor-pointer border border-dashed border-slate-300 hover:border-slate-400 hover:bg-secondary' : ''}`}
                            title={isEditable ? "Clicca per modificare" : ""}>
                            {line.line_notes || '-'}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-sm">{line.unit || '-'}</td>
                      <td className="p-2 text-right font-mono text-sm">{line.quantity}</td>
                      <td className="p-2 text-right font-mono text-sm">
                        {isEditable && isFree ? (
                          <NumberInput
                            step="0.01"
                            min="0"
                            value={line.unit_price_chf ?? null}
                            placeholder={missingPrice ? 'da impostare' : '0.00'}
                            onBlur={(v) => {
                              if (v != null && v !== (line.unit_price_chf ?? null)) {
                                saveLineField(line.id, 'unit_price_chf', v);
                              }
                            }}
                            className={`w-24 h-7 text-right text-sm font-mono ml-auto ${missingPrice ? 'border-amber-400 bg-white' : ''}`}
                            data-testid={`free-line-${idx}-price`}
                          />
                        ) : (
                          line.unit_price_chf != null ? formatCurrency(line.unit_price_chf) : <span className="text-amber-700">—</span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-sm">{line.discount_percent ? `${line.discount_percent}%` : '-'}</td>
                      <td className="p-2 text-right font-mono text-sm">
                        {isEditable && isFree ? (
                          <select
                            value={line.mwst_rate_snapshot != null ? String(line.mwst_rate_snapshot) : ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') return;
                              saveLineField(line.id, 'mwst_rate_snapshot', parseFloat(v));
                            }}
                            className={`h-7 text-sm border rounded px-1 ${missingIva ? 'border-amber-400 bg-white' : 'border-slate-200'}`}
                            data-testid={`free-line-${idx}-iva`}
                          >
                            <option value="" disabled>—</option>
                            <option value="8.1">8.1%</option>
                            <option value="2.6">2.6%</option>
                            <option value="0">0%</option>
                          </select>
                        ) : (
                          line.mwst_rate_snapshot != null ? `${line.mwst_rate_snapshot}%` : <span className="text-amber-700">—</span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-sm font-medium">{line.net_amount != null ? formatCurrency(line.net_amount) : <span className="text-amber-700">—</span>}</td>
                      {isEditable && <td></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 border-t pt-4">
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-600">Netto:</span><span className="font-mono font-medium">{formatCurrency(invoice.net_total)}</span></div>
                {invoice.mwst_breakdown?.filter(b => b.mwst > 0).map((breakdown, idx) => (
                  <div key={idx} className="flex justify-between text-sm"><span className="text-slate-600">IVA ({breakdown.rate}%):</span><span className="font-mono font-medium">{formatCurrency(breakdown.mwst)}</span></div>
                ))}
                <div className="flex justify-between border-t pt-2"><span className="font-semibold">Totale:</span><span className="font-mono font-bold text-lg">{formatCurrency(invoice.gross_total)}</span></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Section */}
      {(invoice.status === 'ISSUED' || invoice.status === 'OVERDUE' || invoice.status === 'SETTLED') && (
        <Card className="mb-6" data-testid="payments-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Pagamenti</CardTitle>
              {invoice.status !== 'SETTLED' && <Button size="sm" onClick={handleSettle}><Plus size={16} className="mr-1" /> Aggiungi pagamento</Button>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Pagato: <b>{formatCurrency(totalPaid)}</b></span>
                <span>Residuo: <b className={remainingAmount > 0 ? 'text-amber-600' : 'text-green-600'}>{formatCurrency(remainingAmount)}</b></span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full transition-all ${paymentPercentage >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${paymentPercentage}%` }}></div>
              </div>
              <p className="text-xs text-slate-400 mt-1">{paymentPercentage.toFixed(0)}% del totale</p>
            </div>
            {payments.length === 0 ? <p className="text-center text-slate-400 text-sm py-4">Nessun pagamento registrato</p> : (
              <div className="space-y-2">
                {payments.map((payment, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                    {payment.method === 'BANK_TRANSFER' ? <Building2 size={16} className="text-blue-500" /> : payment.method === 'CASH' ? <Banknote size={16} className="text-green-500" /> : <CreditCard size={16} className="text-purple-500" />}
                    <div className="flex-1">
                      <span className="font-mono font-medium">{formatCurrency(payment.amount_chf)}</span>
                      <p className="text-xs text-slate-500">{formatDate(payment.payment_date)} - {payment.method === 'BANK_TRANSFER' ? 'Bonifico' : payment.method === 'CASH' ? 'Contanti' : 'Carta'}</p>
                      {payment.notes && <p className="text-xs text-slate-400 mt-0.5">{payment.notes}</p>}
                    </div>
                    <p className="text-xs text-slate-400">{payment.registered_by}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Clock size={20} /> Cronologia</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-slate-400"></div><div><p className="text-sm font-medium">Creata</p><p className="text-xs text-slate-500">{formatDateTime(invoice.created_at)} da {invoice.created_by}</p></div></div>
            {invoice.issue_date && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-blue-500"></div><div><p className="text-sm font-medium text-blue-700">Emessa</p><p className="text-xs text-slate-500">{formatDateTime(invoice.issue_date)}</p></div></div>}
            {invoice.email_sent_at && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-blue-400"></div><div><p className="text-sm font-medium text-blue-600">Email inviata</p><p className="text-xs text-slate-500">{formatDateTime(invoice.email_sent_at)}{invoice.email_sent_by && ` da ${invoice.email_sent_by}`}</p></div></div>}
            {invoice.mail_sent_at && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-blue-400"></div><div><p className="text-sm font-medium text-blue-600">Posta inviata</p><p className="text-xs text-slate-500">{formatDateTime(invoice.mail_sent_at)}{invoice.mail_sent_by && ` da ${invoice.mail_sent_by}`}</p></div></div>}
            {invoice.status === 'OVERDUE' && invoice.due_date && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-red-500"></div><div><p className="text-sm font-medium text-red-700">Scaduta</p><p className="text-xs text-slate-500">Scadenza: {formatDate(invoice.due_date)}{invoice.days_overdue && ` (da ${invoice.days_overdue} giorni)`}</p></div></div>}
            {invoice.settled_at && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-green-500"></div><div><p className="text-sm font-medium text-green-700">Saldata</p><p className="text-xs text-slate-500">{formatDateTime(invoice.settled_at)}{invoice.settled_by && ` da ${invoice.settled_by}`}</p></div></div>}
            {invoice.cancelled_at && <div className="flex items-start gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-slate-500"></div><div><p className="text-sm font-medium text-slate-700">Annullata</p><p className="text-xs text-slate-500">{formatDateTime(invoice.cancelled_at)}{invoice.cancelled_by && ` da ${invoice.cancelled_by}`}</p></div></div>}
          </div>
        </CardContent>
      </Card>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent><DialogHeader><DialogTitle>Fattura Emessa!</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">La fattura {invoice.number} e stata emessa con successo.</p>
          {customer?.invoice_delivery_method && <p className="text-sm">Metodo di invio: <b>{customer.invoice_delivery_method === 'EMAIL' ? 'Email' : customer.invoice_delivery_method === 'PAPER' ? 'Posta' : 'Email + Posta'}</b></p>}
          <div className="space-y-3 mt-4">
            {(customer?.invoice_delivery_method === 'EMAIL' || customer?.invoice_delivery_method === 'BOTH') && (
              customer?.billing_email ? (
                <button onClick={async () => { await handleSendEmail(); if (customer?.invoice_delivery_method !== 'BOTH' || invoice.mail_sent_at) setShowSendDialog(false); }}
                  disabled={sendingEmail || invoice.email_sent_at}
                  className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${invoice.email_sent_at ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'}`}>
                  {invoice.email_sent_at ? <Check size={16} /> : <Mail size={16} />}
                  <span>{invoice.email_sent_at ? 'Email inviata' : sendingEmail ? 'Invio in corso...' : `Invia via Email a ${customer.billing_email}`}</span>
                </button>
              ) : <p className="text-center text-sm text-red-500">Email cliente mancante</p>
            )}
            {(customer?.invoice_delivery_method === 'PAPER' || customer?.invoice_delivery_method === 'BOTH') && (
              <button onClick={async () => { await handleMarkMailSent(); if (customer?.invoice_delivery_method !== 'BOTH' || invoice.email_sent_at || !customer?.billing_email) setShowSendDialog(false); }}
                disabled={markingMail || invoice.mail_sent_at}
                className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${invoice.mail_sent_at ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'}`}>
                {invoice.mail_sent_at ? <Check size={16} /> : <FileText size={16} />}
                <span>{invoice.mail_sent_at ? 'Posta segnata come inviata' : markingMail ? 'Salvo...' : 'Segna come inviato per Posta'}</span>
              </button>
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowSendDialog(false)}>{(invoice.email_sent_at || invoice.mail_sent_at) ? 'Chiudi' : 'Invia piu tardi'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent><DialogHeader><DialogTitle>Registra Pagamento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between"><span>Totale fattura:</span><span className="font-mono font-medium">{formatCurrency(totalInvoice)}</span></div>
              <div className="flex justify-between"><span>Gia pagato:</span><span className="font-mono text-green-600">{formatCurrency(totalPaid)}</span></div>
              <div className="flex justify-between border-t pt-1"><span>Da pagare:</span><span className={`font-mono font-medium ${remainingAmount > 0 ? 'text-amber-600' : 'text-green-600'}`}>{formatCurrency(remainingAmount)}</span></div>
            </div>
            <div className="space-y-2"><Label>Data pagamento</Label><Input type="date" value={paymentData.payment_date} onChange={(e) => setPaymentData(p => ({ ...p, payment_date: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Importo (CHF)</Label><NumberInput step="0.01" min="0" value={paymentData.amount_chf} onChange={(val) => setPaymentData(p => ({ ...p, amount_chf: val }))} /></div>
            <div className="space-y-2"><Label>Metodo di pagamento</Label>
              <Select value={paymentData.method} onValueChange={(v) => setPaymentData(p => ({ ...p, method: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                <SelectItem value="BANK_TRANSFER">Bonifico bancario</SelectItem><SelectItem value="CASH">Contanti</SelectItem><SelectItem value="CARD">Carta</SelectItem>
              </SelectContent></Select>
            </div>
            <div className="space-y-2"><Label>Note (opzionale)</Label><Input value={paymentData.notes} onChange={(e) => setPaymentData(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowPaymentDialog(false)}>Annulla</Button><Button onClick={handleRegisterPayment} disabled={savingPayment}>{savingPayment ? 'Salvataggio...' : 'Registra Pagamento'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
